import { useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import Header from "../../components/content_wrapper/header";
import CustomLaoder from "../../components/customLoader";
import NoRecordFound from "../../components/noRecordFound";
import {
  useGetHostRequests,
  useResolveHostRequest,
} from "../../hooks/hostRequestsApi/getHostRequests";
import Pagination from "../../utils/pagination";

// The API returns snake_case; the axios response interceptor camelCases the
// body, so every field here is read camelCase (host_name -> hostName,
// phone_number -> phoneNumber, user_email -> userEmail, ...).

const STATUS_TABS = [
  { key: "pending", label: "Pending" },
  { key: "resolved", label: "Resolved" },
  { key: "", label: "All" },
];

const HostRequests = () => {
  const [countData, setCountData] = useState<any>(10);
  const [pageNumber, setPageNumber] = useState<any>(1);
  const [searchValue, setSearchValue] = useState<string>("");
  const [finalValue, setFinalValue] = useState<string>("");
  const [status, setStatus] = useState("pending");

  const { data, isLoading } = useGetHostRequests({
    page: pageNumber,
    limit: countData,
    search: finalValue,
    status,
  });
  const resolveRequest = useResolveHostRequest();

  const rows: any[] = data?.data?.hostRequests ?? [];

  const handlePrevPage = () => setPageNumber((p: any) => Math.max(1, p - 1));
  const handleNextPage = () => setPageNumber((p: any) => p + 1);
  const handleRowLimitChange = (v: number) => setCountData(v);

  const setResolved = (item: any, next: "pending" | "resolved") => {
    resolveRequest.mutate(
      { id: item._id, status: next },
      {
        onSuccess: () => {
          toast.success(next === "resolved" ? "Marked resolved" : "Reopened");
        },
        onError: () => {
          toast.error("Could not update the request");
        },
      }
    );
  };

  return (
    <div className="main-wrapper">
      <Header title="Host Slot Requests" />
      <CustomLaoder loader={isLoading}>
        <div className="content-wrapper">
          <section className="main-section spacer">
            <div className="container">
              <p className="text-muted mb-3">
                Owners waiting for a slot to open at a <strong>specific, full</strong>{" "}
                key host (they tapped "notify me" on that host). Different from{" "}
                <em>Key Host Requests</em>, which is owners asking for a host near an
                address where there isn't one. Mark <em>Resolved</em> once you've
                actioned it — freed a slot, contacted the owner, or it's no longer
                relevant.
              </p>

              <ul className="nav nav-pills mb-3" style={{ gap: 6 }}>
                {STATUS_TABS.map((t) => (
                  <li className="nav-item" key={t.key || "all"}>
                    <button
                      className={`nav-link ${status === t.key ? "active" : ""}`}
                      onClick={() => {
                        setStatus(t.key);
                        setPageNumber(1);
                      }}
                    >
                      {t.label}
                    </button>
                  </li>
                ))}
              </ul>

              <form
                className="mb-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  setPageNumber(1);
                  setFinalValue(searchValue.trim());
                }}
              >
                <div className="d-flex gap-2" style={{ maxWidth: 480 }}>
                  <input
                    type="search"
                    className="form-control"
                    placeholder="Search by owner or host email"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                  />
                  <button className="btn primary-btn" type="submit">
                    Apply
                  </button>
                  {finalValue && (
                    <button
                      className="btn secondary-btn"
                      type="button"
                      onClick={() => {
                        setSearchValue("");
                        setFinalValue("");
                        setPageNumber(1);
                      }}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </form>

              {!isLoading && rows.length === 0 ? (
                <NoRecordFound />
              ) : (
                <div className="table-responsive wrapper-card">
                  <table className="table align-middle">
                    <thead>
                      <tr>
                        <th>Owner waiting</th>
                        <th>Wants a slot at</th>
                        <th>Host contact</th>
                        <th>Requested</th>
                        <th>Status</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((item: any) => (
                        <tr key={item._id}>
                          <td className="fw-semibold">{item.userEmail || "—"}</td>
                          <td>
                            <div className="fw-semibold text-capitalize">
                              {item.storeName || item.hostName || "—"}
                            </div>
                            {item.address && (
                              <div className="text-muted small">{item.address}</div>
                            )}
                          </td>
                          <td className="small">
                            {item.hostEmail && <div>{item.hostEmail}</div>}
                            {(item.countryCode || item.phoneNumber) && (
                              <div className="text-muted">
                                {[item.countryCode, item.phoneNumber]
                                  .filter(Boolean)
                                  .join(" ")}
                              </div>
                            )}
                            {!item.hostEmail && !item.phoneNumber && (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td
                            className="text-muted small"
                            style={{ whiteSpace: "nowrap" }}
                          >
                            {item.createdAt
                              ? moment.utc(item.createdAt).fromNow()
                              : "—"}
                          </td>
                          <td>
                            <span
                              className={`badge ${
                                item.status === "resolved"
                                  ? "text-bg-success"
                                  : "text-bg-warning"
                              }`}
                            >
                              {item.status === "resolved" ? "Resolved" : "Pending"}
                            </span>
                            {item.resolvedAt && (
                              <div className="text-muted small">
                                {moment.utc(item.resolvedAt).format("MMM D")}
                              </div>
                            )}
                          </td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            {item.status === "resolved" ? (
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                disabled={resolveRequest.isLoading}
                                onClick={() => setResolved(item, "pending")}
                              >
                                Reopen
                              </button>
                            ) : (
                              <button
                                className="btn btn-sm btn-success"
                                disabled={resolveRequest.isLoading}
                                onClick={() => setResolved(item, "resolved")}
                              >
                                Mark resolved
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {data?.status !== false && data?.data?.totalRecords > countData && (
                <Pagination
                  totalPages={data?.data?.totalPages}
                  currentPage={pageNumber}
                  handlePrevPage={handlePrevPage}
                  handleNextPage={handleNextPage}
                  handleRowLimitChange={handleRowLimitChange}
                />
              )}
            </div>
          </section>
        </div>
      </CustomLaoder>
    </div>
  );
};

export default HostRequests;
